import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';

import { WelcomePage } from '@/pages/WelcomePage';
import { RegisterPage } from '@/pages/RegisterPage';
import { MenuPage } from '@/pages/MenuPage';
import { LeaderboardPage } from '@/pages/LeaderboardPage';
import { LoginPage } from '@/pages/LoginPage';

/* ─── Lazy-loaded por chunk (Fase 6) ────────────────────────────── */
const Game1Page = lazy(() =>
  import('@/features/game1-speed-data/Game1Page').then((m) => ({ default: m.Game1Page })),
);
const Game3Page = lazy(() =>
  import('@/features/game3-cases/Game3Page').then((m) => ({ default: m.Game3Page })),
);
const Game4Page = lazy(() =>
  import('@/features/game4-pokemon/Game4Page').then((m) => ({ default: m.Game4Page })),
);
const Game1ResultPage = lazy(() =>
  import('@/pages/Game1ResultPage').then((m) => ({ default: m.Game1ResultPage })),
);
const Game3ResultPage = lazy(() =>
  import('@/pages/Game3ResultPage').then((m) => ({ default: m.Game3ResultPage })),
);
const Game4ResultPage = lazy(() =>
  import('@/pages/Game4ResultPage').then((m) => ({ default: m.Game4ResultPage })),
);

/* Admin: chunk separado, sólo se carga si entras a /admin */
const AdminLoginPage = lazy(() =>
  import('@/features/admin/AdminLoginPage').then((m) => ({ default: m.AdminLoginPage })),
);
const AdminLayout = lazy(() =>
  import('@/features/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })),
);
const AdminGate = lazy(() =>
  import('@/features/admin/AdminGate').then((m) => ({ default: m.AdminGate })),
);
const QuestionsAdmin = lazy(() =>
  import('@/features/admin/QuestionsAdmin').then((m) => ({ default: m.QuestionsAdmin })),
);
const ScenariosAdmin = lazy(() =>
  import('@/features/admin/ScenariosAdmin').then((m) => ({ default: m.ScenariosAdmin })),
);
const CasesAdmin = lazy(() =>
  import('@/features/admin/CasesAdmin').then((m) => ({ default: m.CasesAdmin })),
);
const PokemonAdmin = lazy(() =>
  import('@/features/admin/PokemonAdmin').then((m) => ({ default: m.PokemonAdmin })),
);

/* ─── Helpers ──────────────────────────────────────────────────── */

function ChunkFallback() {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, display: 'grid', placeItems: 'center',
        background: '#070a1c', color: '#aeb6e6',
        fontFamily: 'Chakra Petch', letterSpacing: 4,
      }}
    >
      Cargando...
    </div>
  );
}

function GameDispatcher() {
  const { id } = useParams<{ id: string }>();
  switch (id) {
    case 'g1': return <Game1Page />;
    case 'g3': return <Game3Page />;
    case 'g4': return <Game4Page />;
    // g2 (Arkeflow) retirado del evento → al menú
    default:   return <Navigate to="/menu" replace />;
  }
}

function ResultDispatcher() {
  const { id } = useParams<{ id: string }>();
  switch (id) {
    case 'g1': return <Game1ResultPage />;
    case 'g3': return <Game3ResultPage />;
    case 'g4': return <Game4ResultPage />;
    default:   return <Navigate to="/menu" replace />;
  }
}

export function AppRouter() {
  return (
    <Suspense fallback={<ChunkFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<WelcomePage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/menu" element={<MenuPage />} />
        <Route path="/game/:id" element={<GameDispatcher />} />
        <Route path="/result/:id" element={<ResultDispatcher />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />

        {/* Admin */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route
          path="/admin"
          element={
            <AdminGate>
              <AdminLayout />
            </AdminGate>
          }
        >
          <Route index element={<Navigate to="/admin/questions" replace />} />
          <Route path="questions" element={<QuestionsAdmin />} />
          <Route path="scenarios" element={<ScenariosAdmin />} />
          <Route path="cases" element={<CasesAdmin />} />
          <Route path="pokemon" element={<PokemonAdmin />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
